import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { DeleteConfirmationModal } from "@/modules/review/components/delete-confirmation-modal";
import Container from "@/components/ui/container";
import Button from "@/components/ui/button";
import { useReviews } from "@/modules/review/hooks/use-reviews";
import { useAuth } from "@/modules/auth";
import type { ReviewResponse, ReviewTag } from "@/modules/review/types";
import IconStarFill from "@/components/icons/icon-star-fill";
import IconStarLine from "@/components/icons/icon-star-line";
import AudioPlayer from "@/components/ui/audio-player";
import VideoModal from "@/modules/review/components/video-modal";

type ReviewWithTags = ReviewResponse & { tags?: ReviewTag[]; audio_url?: string };

function StarRatingDisplay({ rating }: { rating: number | null }) {
	if (!rating) return <span className="text-sm text-tertiary">No rating provided</span>;
	return (
		<div className="flex items-center gap-0.5">
			{[1, 2, 3, 4, 5].map((star) => (star <= rating ? <IconStarFill key={star} className="w-6 h-6 text-brand" /> : <IconStarLine key={star} className="w-6 h-6 text-gray-300" />))}
		</div>
	);
}

function ContentTypeBadge({ contentType }: { contentType: string }) {
	const config: Record<string, { label: string; className: string }> = {
		text: { label: "Text", className: "bg-blue-50 text-blue-700 border-blue-200" },
		video: { label: "Video", className: "bg-purple-50 text-purple-700 border-purple-200" },
		audio: { label: "Audio", className: "bg-orange-50 text-orange-700 border-orange-200" },
	};
	const { label, className } = config[contentType] || { label: contentType, className: "bg-gray-100 text-gray-600 border-gray-200" };
	return <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${className}`}>{label}</span>;
}

function ReadOnlyField({ label, value }: { label: string; value: string | null | undefined }) {
	return (
		<div className="space-y-1.5">
			<label className="block text-xs font-medium text-tertiary uppercase tracking-wide">{label}</label>
			<div className="text-sm text-primary">{value || "—"}</div>
		</div>
	);
}

function formatDate(dateStr: string | undefined): string {
	if (!dateStr) return "—";
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-US", {
		year: "numeric",
		month: "long",
		day: "numeric",
	});
}

export default function ViewDraftReviewPage() {
	const navigate = useNavigate();
	const { brandUrl, reviewId } = useParams();
	const { currentWorkspaceId } = useAuth();
	const { showReview, deleteReview } = useReviews();

	const [review, setReview] = useState<ReviewWithTags | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [videoModalOpen, setVideoModalOpen] = useState(false);

	useEffect(() => {
		const fetchReview = async () => {
			if (!currentWorkspaceId || !reviewId) return;

			setIsLoading(true);
			setError(null);

			try {
				const result = await showReview(currentWorkspaceId, reviewId);
				if (result) {
					setReview(result as ReviewWithTags);
				} else {
					setError("Review not found");
				}
			} catch (err) {
				setError("Failed to load review");
			} finally {
				setIsLoading(false);
			}
		};

		fetchReview();
	}, [currentWorkspaceId, reviewId, showReview]);

	const handleBack = () => {
		navigate(`/${brandUrl}/reviews/drafts`);
	};

	const handleEdit = () => {
		navigate(`/${brandUrl}/reviews/${reviewId}/edit`);
	};

	const handleDeleteConfirm = async () => {
		if (!currentWorkspaceId || !reviewId) return;

		setIsDeleting(true);
		try {
			const result = await deleteReview(currentWorkspaceId, reviewId);
			if (result) {
				setDeleteModalOpen(false);
				navigate(`/${brandUrl}/reviews`);
			}
		} catch (err) {
			console.error("Failed to delete review:", err);
		} finally {
			setIsDeleting(false);
		}
	};

	if (isLoading) {
		return (
			<Container className="py-12">
				<div className="flex items-center justify-center min-h-[400px]">
					<div className="flex flex-col items-center gap-3">
						<svg className="w-8 h-8 text-brand animate-spin" fill="none" viewBox="0 0 24 24">
							<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
							<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
						</svg>
						<span className="text-tertiary">Loading incomplete submission...</span>
					</div>
				</div>
			</Container>
		);
	}

	if (error) {
		return (
			<Container className="py-12">
				<div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
					<div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-2">
						<svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={1.5}
								d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
							/>
						</svg>
					</div>
					<h2 className="text-lg font-semibold text-primary">Error loading incomplete submission</h2>
					<p className="text-tertiary">{error}</p>
					<button onClick={handleBack} className="text-brand hover:underline mt-2">
						Back to reviews
					</button>
				</div>
			</Container>
		);
	}

	if (!review) {
		return (
			<Container className="py-12">
				<div className="flex flex-col items-center justify-center min-h-100 gap-4">
					<div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-2">
						<svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
						</svg>
					</div>
					<h2 className="text-lg font-semibold text-primary">Incomplete submission not found</h2>
					<p className="text-tertiary">This incomplete submission may have been deleted or does not exist.</p>
					<button onClick={handleBack} className="text-brand hover:underline mt-2">
						Back to reviews
					</button>
				</div>
			</Container>
		);
	}

	return (
		<>
			<Container className="pt-12 pb-24">
				{/* Page Header */}
				<div className="mb-8 flex items-center justify-between">
					<div className="flex items-center gap-4">
						<button onClick={handleBack} className="flex items-center gap-2 text-tertiary hover:text-primary transition-colors">
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
							</svg>
							<span>Back</span>
						</button>
						<div className="w-px h-6 bg-gray-200" />
						<h1 className="text-xl font-semibold text-primary">Incomplete Submission</h1>
						<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Draft</span>
					</div>
				</div>

				{/* Main Content */}
				<div className="space-y-6">
					{/* Basic Information Card */}
					<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
						<div className="flex items-start gap-3 p-6 border-b border-gray-200">
							<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
								<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.5}
										d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
									/>
								</svg>
							</div>
							<div>
								<h3 className="text-t-15 font-semibold text-primary">Submission Information</h3>
								<p className="text-t-13 text-tertiary mt-0.5">Basic details about this incomplete submission</p>
							</div>
						</div>

						<div className="p-6 space-y-6">
							{/* Content Type and Source */}
							<div className="flex items-center gap-6">
								<div className="space-y-1.5">
									<label className="block text-xs font-medium text-tertiary uppercase tracking-wide">Content Type</label>
									<ContentTypeBadge contentType={review.content_type} />
								</div>
							</div>

							{/* Rating */}
							<div className="space-y-1.5">
								<label className="block text-xs font-medium text-tertiary uppercase tracking-wide">Rating</label>
								<StarRatingDisplay rating={review.rating} />
							</div>

							{/* Form */}
							{review.form?.title && (
								<div className="space-y-1.5">
									<label className="block text-xs font-medium text-tertiary uppercase tracking-wide">Form</label>
									<a
										href={`/${review.workspace_id}/forms/${review.form.id}/edit`}
										className="text-sm text-brand hover:underline flex items-center gap-1"
										target="_blank"
										rel="noopener noreferrer"
									>
										{review.form.title}
										<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
										</svg>
									</a>
								</div>
							)}

							{/* Date */}
							<ReadOnlyField label="Submission Date" value={formatDate(review.created_at)} />
						</div>
					</div>

					{/* Customer Information Card */}
					<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
						<div className="flex items-start gap-3 p-6 border-b border-gray-200">
							<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
								<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
								</svg>
							</div>
							<div>
								<h3 className="text-t-15 font-semibold text-primary">Customer Information</h3>
								<p className="text-t-13 text-tertiary mt-0.5">Details about the person who submitted this review</p>
							</div>
						</div>

						<div className="p-6">
							<div className="grid grid-cols-2 gap-6">
								<ReadOnlyField label="Name" value={review.customer_name} />
								<ReadOnlyField label="Email" value={review.customer_email} />
								<ReadOnlyField label="Job Title" value={review.customer_job_title} />
								<ReadOnlyField label="Company" value={review.customer_company_name} />
							</div>
						</div>
					</div>

					{/* Video Card */}
					{review.content_type === "video" && review.mux_playback_id && (
						<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
							<div className="flex items-start gap-3 p-6 border-b border-gray-200">
								<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
									<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={1.5}
											d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
										/>
									</svg>
								</div>
								<div>
									<h3 className="text-t-15 font-semibold text-primary">Video</h3>
									<p className="text-t-13 text-tertiary mt-0.5">Video testimonial recorded by the customer</p>
								</div>
							</div>

							<div className="p-6">
								<div className="rounded-lg overflow-hidden border border-gray-200 bg-gray-900">
									<div className="aspect-video relative">
										{review.video_thumbnail_url ? (
											<img src={review.video_thumbnail_url} alt="Video thumbnail" className="w-full h-full object-cover" />
										) : (
											<img src={`https://image.mux.com/${review.mux_playback_id}/thumbnail.jpg?time=0`} alt="Video thumbnail" className="w-full h-full object-cover" />
										)}
										<div className="absolute inset-0 flex items-center justify-center bg-black/30">
											<button
												type="button"
												onClick={() => setVideoModalOpen(true)}
												className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors cursor-pointer"
											>
												<svg className="w-8 h-8 text-gray-600 ml-1" fill="currentColor" viewBox="0 0 24 24">
													<path d="M8 5v14l11-7z" />
												</svg>
											</button>
										</div>
									</div>
								</div>
								<VideoModal open={videoModalOpen} onOpenChange={setVideoModalOpen} playbackId={review.mux_playback_id} />
							</div>
						</div>
					)}

					{/* Audio Card */}
					{review.content_type === "audio" && review.audio_url && (
						<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
							<div className="flex items-start gap-3 p-6 border-b border-gray-200">
								<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
									<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={1.5}
											d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
										/>
									</svg>
								</div>
								<div>
									<h3 className="text-t-15 font-semibold text-primary">Audio</h3>
									<p className="text-t-13 text-tertiary mt-0.5">Audio testimonial recorded by the customer</p>
								</div>
							</div>

							<div className="p-6">
								<AudioPlayer src={review.audio_url} initialDuration={review.audio_duration} />
							</div>
						</div>
					)}

					{/* Content Card */}
					{review.content_text && (
						<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
							<div className="flex items-start gap-3 p-6 border-b border-gray-200">
								<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
									<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={1.5}
											d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
										/>
									</svg>
								</div>
								<div>
									<h3 className="text-t-15 font-semibold text-primary">Content</h3>
									<p className="text-t-13 text-tertiary mt-0.5">The review content provided by the customer</p>
								</div>
							</div>

							<div className="p-6">
								<div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
									<p className="text-sm text-primary whitespace-pre-wrap leading-relaxed">{review.content_text}</p>
								</div>
							</div>
						</div>
					)}
				</div>
			</Container>

			{/* Sticky footer with action buttons */}
			<div className="fixed bottom-0 left-0 z-20 bg-white border-t border-gray-200 px-8 py-4 w-[calc(100%-256px)] ml-64">
				<Container>
					<div className="flex items-center justify-between">
						<button onClick={handleBack} className="flex items-center gap-2 text-tertiary hover:text-primary transition-colors">
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
							</svg>
							<span>Back to Incomplete Submissions</span>
						</button>
						<div className="flex items-center gap-3">
							<Button type="button" variant="light" onClick={() => setDeleteModalOpen(true)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
								Delete
							</Button>
							<Button type="button" variant="default" onClick={handleEdit}>
								Continue Editing
							</Button>
						</div>
					</div>
				</Container>
			</div>

			{/* Delete Confirmation Modal */}
			<DeleteConfirmationModal
				open={deleteModalOpen}
				onOpenChange={setDeleteModalOpen}
				onConfirm={handleDeleteConfirm}
				isLoading={isDeleting}
				title="Delete Incomplete Submission"
				description="Are you sure you want to delete this incomplete submission? This action cannot be undone."
				confirmText="Delete Submission"
			/>
		</>
	);
}
